
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const ModernFarmhouseADUDesignCupertino = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Modern Farmhouse ADU Design & Construction in Cupertino",
    "description": "Custom Modern Farmhouse ADUs in Cupertino. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/modern-farmhouse-adu-design-cupertino"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/modern-farmhouse-adu-design-cupertino" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Modern Farmhouse ADU Design & Construction in Cupertino
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Luxury Modern Farmhouse accessory dwelling units designed for Cupertino's affluent tech professionals and families.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Modern Farmhouse ADUs in Cupertino?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Executive Comfort</h3>
              <p className="text-gray-300">Sophisticated rustic design perfect for Cupertino's tech executives seeking peaceful home retreats.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Premium Materials</h3>
              <p className="text-gray-300">High-end finishes and custom details that meet Cupertino's luxury residential standards.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Investment Excellence</h3>
              <p className="text-gray-300">Timeless style that maintains exceptional value in Cupertino's prestigious real estate market.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Modern Farmhouse ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Board and batten or shiplap siding</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Metal roofing with exposed beams</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Large front porches with columns</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Barn doors and rustic hardware</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Open kitchens with farmhouse sinks</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Cupertino-Specific Modern Farmhouse ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              In Cupertino, Modern Farmhouse ADUs must comply with specific local requirements:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Luxury neighborhood aesthetic standards</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Cupertino design review requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Premium building material specifications</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Advanced smart home technology integration</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our team navigates Cupertino's unique requirements to deliver stunning Modern Farmhouse ADUs.
            </p>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Modern Farmhouse ADU Cost in Cupertino
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Modern Farmhouse ADU in Cupertino?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Let's discuss your Modern Farmhouse ADU project and create something charming for your Cupertino property.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in Cupertino:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/cupertino-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in Cupertino
              </Link>
              <Link to="/adu-cost-cupertino" className="text-[#FF7A00] hover:underline">
                ADU Cost in Cupertino
              </Link>
              <Link to="/adu-regulations-cupertino" className="text-[#FF7A00] hover:underline">
                ADU Regulations in Cupertino
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Modern Farmhouse ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/modern-farmhouse-adu-design-san-jose" className="text-[#FF7A00] hover:underline">
                Modern Farmhouse ADU San Jose
              </Link>
              <Link to="/modern-farmhouse-adu-design-sunnyvale" className="text-[#FF7A00] hover:underline">
                Modern Farmhouse ADU Sunnyvale
              </Link>
              <Link to="/modern-farmhouse-adu-design-palo-alto" className="text-[#FF7A00] hover:underline">
                Modern Farmhouse ADU Palo Alto
              </Link>
              <Link to="/modern-farmhouse-adu-design-mountain-view" className="text-[#FF7A00] hover:underline">
                Modern Farmhouse ADU Mountain View
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default ModernFarmhouseADUDesignCupertino;
